/**
 * The signed-in user's single saved signature, carried to and from the Sparta
 * API as a base64-encoded PNG with NO data-URI prefix. Persisted once per user
 * and auto-applied (with a per-submit confirmation) on the foreman time-entry
 * screens so the user does not re-draw it every time.
 *
 * Contract note: the API stores and returns BARE base64. Every client drawing
 * surface (signature_pad on web, the DynamicRender signature control on mobile)
 * speaks data-URI strings (`data:image/png;base64,...`). Use the helpers below
 * to convert at the boundary, never inline the prefix handling.
 */
export interface ForemanUserSignatureDto {
  /** Bare base64-encoded PNG bytes, no `data:` prefix. */
  signatureBase64: string;
  /** MIME type of the stored image, e.g. `image/png`. */
  contentType: string;
  modifiedDate?: Date | string | null;
}

const DATA_URL_PREFIX = /^data:[^;]*;base64,/;
const DEFAULT_CONTENT_TYPE = 'image/png';

/**
 * Strip a `data:<mime>;base64,` prefix off a signature string so it matches the
 * API's bare-base64 contract. Idempotent: a string that is already bare is
 * returned unchanged. Nullish input yields an empty string.
 */
export function toBareSignatureBase64(
  value: string | null | undefined
): string {
  if (!value) {
    return '';
  }
  return value.replace(DATA_URL_PREFIX, '');
}

/**
 * Build a `data:<contentType>;base64,<bytes>` data-URI from a saved signature
 * DTO so it can be loaded into a drawing surface. Returns null when there is no
 * stored signature. Missing/blank contentType defaults to `image/png`.
 */
export function toSignatureDataUrl(
  dto: ForemanUserSignatureDto | null | undefined
): string | null {
  if (!dto || !dto.signatureBase64) {
    return null;
  }
  const contentType = dto.contentType?.trim() || DEFAULT_CONTENT_TYPE;
  const bytes = toBareSignatureBase64(dto.signatureBase64);
  return `data:${contentType};base64,${bytes}`;
}
